'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * QR-код текущей страницы.
 *
 * Человек читает страницу на компьютере, а продолжить хочет с телефона:
 * набирать адрес руками он не будет, а переслать себе ссылку — это ещё
 * один мессенджер и ещё одно переключение. Код снимает оба шага.
 *
 * Адрес берётся из `location` уже после появления страницы, а не из
 * пропсов: подвал общий для всех страниц домена, и один зашитый адрес
 * вёл бы с любой страницы на главную. Слушаем и смену адреса: переходы
 * внутри сайта происходят без перезагрузки, и без пересчёта код показывал
 * бы предыдущую страницу.
 *
 * Библиотека подключается динамически: она уже есть в проекте (QR оплаты
 * и генератор DataHunter), но статический импорт в подвале утянул бы её
 * в загрузку каждой страницы сайта.
 */
export function PageQr({ label }: { label: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [href, setHref] = useState('');

  useEffect(() => {
    // Адрес читается на клиенте: на сервере `location` нет, а
    // отрисованный с сервера код был бы кодом чужой страницы.
    setHref(window.location.href);
  }, []);

  useEffect(() => {
    const target = box.current;
    if (!target || !href) return;

    // Отмена через объект, а не булевой переменной: замыкание
    // обновляется после `await`, и по одному флагу анализатор считает
    // условие всегда ложным.
    const run = { cancelled: false };

    void (async () => {
      try {
        const { default: QRCodeStyling } = await import('qr-code-styling');
        if (run.cancelled) return;

        // Чёрный на белом, а не цвета темы: инвертированный код многие
        // камеры не распознают, и на тёмной теме он был бы украшением.
        const qr = new QRCodeStyling({
          width: 132,
          height: 132,
          data: href,
          type: 'canvas',
          margin: 6,
          qrOptions: { typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'M' },
          dotsOptions: { type: 'square', color: '#000000' },
          backgroundOptions: { color: '#ffffff' },
        });

        target.innerHTML = '';
        qr.append(target);
      } catch {
        // Код — удобство поверх адреса, который и так в адресной строке.
        // Его отказ не должен ломать подвал.
        if (!run.cancelled) setFailed(true);
      }
    })();

    return () => {
      run.cancelled = true;
      target.innerHTML = '';
    };
  }, [href]);

  if (failed) return null;

  return (
    <div className="flex flex-col items-start gap-2">
      {/* Белая подложка обязательна: на тёмной теме код без неё
          сливается с фоном и камерой не читается. */}
      <div
        ref={box}
        data-testid="ais-page-qr"
        aria-label={label}
        role="img"
        className="rounded-lg bg-white p-2 leading-none"
      />
      <p className="max-w-[9.5rem] text-xs text-[var(--color-text-muted)] leading-snug">{label}</p>
    </div>
  );
}
